import { useState } from 'react';
import { arrivesBeforeLeaves, instantOfLabDay } from '@/domain';
import { strings } from '@/shared/strings';
import { formatDay } from '@/shared/format/instant';
import { ConfirmSheet } from '@/ui/primitives/ConfirmSheet';
import { modalStyles } from '@/ui/primitives/Modal';

export interface DeliveredDialogProps {
  jobOrderNo: string;
  /** When it left, so a received day before it is refused here as well as on the server. */
  dispatchedAt: string | null;
  busy: boolean;
  /** What the server said to the last try, in its own words, or nothing. */
  refusal?: string | null;
  onCancel: () => void;
  onConfirm: (input: { deliveredAt?: string; receivedBy?: string }) => void;
}

/**
 * The customer has it. Request of 13 September 2026, section 1.6.
 *
 * A day, defaulting to today, and who signed if anybody knows. It does not move
 * the sales report, which bills the day the case left the lab.
 *
 * Since the request of 5 October 2026 (third), sections 2 and 11, this is the
 * confirmation of Mark received and not a form in front of one: built on the
 * shared `ConfirmSheet`, a bottom sheet on a phone, with the question said in
 * plain words and the day and the signer under it, both optional, so marking a
 * case received today is still one answer. A day before the hand-over is
 * refused here, with a shake and the reason; the server's refusal is shown in
 * the same place and the sheet stays to be answered again. Opened through
 * `useMarkReceived`, which sends the mark and offers Undo after it.
 */
export function DeliveredDialog({
  jobOrderNo,
  dispatchedAt,
  busy,
  refusal = null,
  onCancel,
  onConfirm,
}: DeliveredDialogProps) {
  const today = formatDay(new Date().toISOString());
  const [day, setDay] = useState(today);
  const [receivedBy, setReceivedBy] = useState('');
  const [error, setError] = useState<string | null>(null);

  function confirm(): boolean {
    const chosen = day === '' ? today : day;
    const deliveredAt = instantOfLabDay(chosen, today, new Date().toISOString());
    if (arrivesBeforeLeaves(dispatchedAt, deliveredAt)) {
      setError(strings.shipping.arrivesBeforeLeaves);
      return false;
    }
    setError(null);
    onConfirm({
      // Today is now, which is what the server stamps without being told.
      ...(chosen === today ? {} : { deliveredAt }),
      ...(receivedBy.trim() === '' ? {} : { receivedBy: receivedBy.trim() }),
    });
    return true;
  }

  // The day chosen here is checked first; what the server says is about the last try.
  const shown = error ?? refusal;

  return (
    <ConfirmSheet
      title={`${strings.shipping.receivedTitle} ${jobOrderNo}`}
      statement={strings.confirm.received(jobOrderNo)}
      confirmLabel={strings.shipping.receivedConfirm}
      cancelLabel={strings.shipping.cancel}
      closeLabel={strings.sheet.close}
      onConfirm={confirm}
      onCancel={onCancel}
      busy={busy}
    >
      <div className={modalStyles.field}>
        <label className={modalStyles.label} htmlFor="received-on">
          {strings.shipping.receivedOn}
        </label>
        <input
          id="received-on"
          type="date"
          className={modalStyles.input}
          value={day}
          max={today}
          onChange={(event) => {
            setDay(event.target.value);
            setError(null);
          }}
        />
      </div>

      <div className={modalStyles.field}>
        <label className={modalStyles.label} htmlFor="received-by">
          {strings.shipping.receivedByLabel}
        </label>
        <input
          id="received-by"
          className={modalStyles.input}
          value={receivedBy}
          placeholder={strings.shipping.receivedByPlaceholder}
          autoComplete="off"
          onChange={(event) => setReceivedBy(event.target.value)}
          onKeyDown={(event) => {
            // Enter answers the question from the desk, as the button does.
            if (event.key === 'Enter' && !busy) confirm();
          }}
        />
        <span className={modalStyles.hint}>{strings.shipping.receivedLead}</span>
      </div>

      {shown !== null && (
        <p className={modalStyles.error} role="alert">
          {shown}
        </p>
      )}
    </ConfirmSheet>
  );
}
